import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, shortAddr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Flame, Skull, Coins } from "lucide-react";
import { toast } from "sonner";

const EMOJI = ["🔥", "💀", "🪦", "🪙", "📉", "🧎", "🫡", "😭", "🤡", "⚰️"];

export default function CommunityWall() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ wallet: "", handle: "", content: "" });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const r = await api.get("/community/posts"); setPosts(r.data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wallet.trim() || !form.content.trim()) {
      toast.error("wallet + content required"); return;
    }
    setLoading(true);
    try {
      await api.post("/community/posts", form);
      toast.success("story planted at the campfire");
      setForm({ ...form, content: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "post failed");
    } finally { setLoading(false); }
  };

  const react = async (id, kind) => {
    try {
      await api.post(`/community/posts/${id}/react?kind=${kind}`);
      setPosts((ps) => ps.map((p) => p.id === id ? { ...p, reactions: { ...p.reactions, [kind]: (p.reactions?.[kind] || 0) + 1 } } : p));
    } catch {}
  };

  const addEmoji = (e) => setForm((f) => ({ ...f, content: (f.content || "") + e }));

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <div className="text-xs uppercase tracking-widest text-[#FFC940]">survivor wall</div>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black">tell us how you <span className="ember-text">got here</span></h1>

      <form onSubmit={submit} className="mt-6 rounded-xl border border-[#3A2E1C] bg-[#141008] p-5" data-testid="wall-post-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <Input data-testid="wall-wallet" value={form.wallet} onChange={(e) => setForm({ ...form, wallet: e.target.value })} placeholder="your wallet (0x… or sol:…)" className="bg-[#0A0906] border-[#3A2E1C]" />
          <Input data-testid="wall-handle" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })} placeholder="handle (optional)" className="bg-[#0A0906] border-[#3A2E1C]" />
        </div>
        <Textarea data-testid="wall-content" maxLength={600} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="post your rekt story · text + emoji only · max 600 chars" className="mt-3 bg-[#0A0906] border-[#3A2E1C] min-h-[100px]" />
        <div className="mt-2 flex flex-wrap gap-1">
          {EMOJI.map((e) => (
            <button key={e} type="button" onClick={() => addEmoji(e)} className="h-9 w-9 rounded-md border border-[#3A2E1C] bg-[#1E1810] hover:bg-[#2a241e] text-lg" data-testid={`wall-emoji-${e}`}>{e}</button>
          ))}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-stone-500">{form.content.length}/600</span>
            <Button type="submit" disabled={loading} data-testid="wall-submit" className="bg-[#FFAB00] hover:bg-[#FFC940] text-[#0A0906] font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flame className="w-4 h-4 mr-2" />}
              light it up
            </Button>
          </div>
        </div>
      </form>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {posts.map((p) => (
          <article key={p.id} data-testid="wall-post" className="rounded-xl border border-[#3A2E1C] bg-[#141008] p-5 hover:border-[#FFAB00]/40 transition-all">
            <div className="flex items-center gap-2 text-xs">
              <span className="mono text-stone-400">{shortAddr(p.wallet)}</span>
              {p.handle && <span className="text-[#FFD54F]">· @{p.handle}</span>}
              <span className="ml-auto text-stone-500">{new Date(p.created_at).toLocaleDateString()}</span>
            </div>
            <p className="mt-3 text-stone-200 leading-relaxed whitespace-pre-wrap break-words">{p.content}</p>
            <div className="mt-4 flex gap-2">
              <ReactBtn onClick={() => react(p.id, "fire")} icon={Flame} count={p.reactions?.fire || 0} testId={`react-fire-${p.id}`} />
              <ReactBtn onClick={() => react(p.id, "skull")} icon={Skull} count={p.reactions?.skull || 0} testId={`react-skull-${p.id}`} />
              <ReactBtn onClick={() => react(p.id, "coin")} icon={Coins} count={p.reactions?.coin || 0} testId={`react-coin-${p.id}`} />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function ReactBtn({ onClick, icon: Icon, count, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[#3A2E1C] bg-[#0A0906] hover:border-[#FFAB00]/50 text-xs mono text-stone-300">
      <Icon className="w-3.5 h-3.5 text-[#FFC940]" /> {count}
    </button>
  );
}
